import { useTranslation } from 'react-i18next';

export interface AccountsBulkAction {
    key: string;
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    /** Present when disabled and the reason is worth surfacing (title + a note line). */
    disabledReason?: string;
    danger?: boolean;
}

export interface AccountsBulkBarProps {
    /** Total rows currently ticked (may span pages via "select all matching"). */
    selectedCount: number;
    /** Optional "Chọn tất cả N dòng khớp bộ lọc" control — org-only (realm has no
     *  cross-page selection: its rows are already fully fetched). */
    selectAllMatching?: {
        totalCount: number;
        onSelectAll: () => void;
        busy: boolean;
        /** True once `selectedCount` already covers `totalCount` — hides the button. */
        allSelected: boolean;
    };
    actions: AccountsBulkAction[];
    onClear: () => void;
    busy?: boolean;
    /** Extra honesty lines under the actions (capped walk, cross-org rows excluded, …). */
    notes?: string[];
}

/**
 * EAA-1820: the ONE bulk-selection bar. Replaces the realm's
 * `accounts/AccountsBulkBar.tsx` (2 actions, no cross-page select) and the org's
 * `hierarchy/scopedAccounts/ScopedAccountsBulkBar.tsx` (4 actions, 3 of them
 * permanently disabled with a real backend reason, plus "select all matching").
 * Both screens hand this component a plain `actions` list instead of hard-coding
 * which buttons exist — a disabled action with `disabledReason` renders exactly
 * like today's honestly-inert org buttons, and a context that has none simply
 * passes fewer entries.
 */
export function AccountsBulkBar({
    selectedCount,
    selectAllMatching,
    actions,
    onClear,
    busy,
    notes,
}: AccountsBulkBarProps) {
    const { t } = useTranslation();
    if (selectedCount === 0) return null;

    return (
        <div className="portal-accounts-bulk-bar">
            <span className="portal-accounts-bulk-count">
                {t('console.accounts.bulkBar.selectedCount', { count: selectedCount })}
            </span>
            <span className="portal-accounts-bulk-divider" />
            {selectAllMatching && !selectAllMatching.allSelected && (
                <button
                    type="button"
                    className="portal-accounts-bulk-action"
                    onClick={selectAllMatching.onSelectAll}
                    disabled={selectAllMatching.busy}
                >
                    {selectAllMatching.busy
                        ? t('console.accounts.bulkBar.selectAllBusy')
                        : t('console.accounts.bulkBar.selectAllButton', {
                              count: selectAllMatching.totalCount,
                          })}
                </button>
            )}
            {actions.map((action) => (
                <button
                    key={action.key}
                    type="button"
                    className={'portal-accounts-bulk-action' + (action.danger ? ' is-danger' : '')}
                    onClick={action.onClick}
                    disabled={action.disabled || busy}
                    title={action.disabledReason}
                >
                    {action.label}
                </button>
            ))}
            <span className="portal-accounts-bulk-spacer" />
            <button
                type="button"
                className="portal-accounts-bulk-clear"
                onClick={onClear}
                disabled={busy}
            >
                {t('console.accounts.bulkBar.clearAction')}
            </button>
            {notes?.map((note) => (
                <p key={note} className="portal-accounts-bulk-note">
                    {note}
                </p>
            ))}
        </div>
    );
}

export default AccountsBulkBar;
